import { FC, useMemo } from 'react';
import { useWatch } from 'react-hook-form';

import { EditIcon } from '../../../../../../../assets/icons/index';
import { AppFormattedMessage } from '../../../../../../../components/app-formatted-message/index';
import { SummaryItem } from '../../../../../../../components/summary-list/index';
import { useLocale } from '../../../../../../../hooks/index';
import { templateSignatoryTypeTranslation } from '../../../../constants/template-signatory-type.constant';
import { PopupType } from '../../../../enums/popup-type.const';
import {
  categoryLabelKey,
  expirationPeriodLabelKey,
  SigningMode,
  signingModeLabelKey,
  subCategoryLabelKey,
} from '../../../../enums/template.const';
import { MappingEditorStore } from '../../../../store/mapping-editor.store';
import { TemplatePopupStore } from '../../../../store/template-popup.store';
import { StepContentProps } from '../../../../types/template.types';
import { cloneStepThreeSnapshot } from '../../../../utils/clone-step-three-snapshot.util';
import { downloadFile } from '../../../../utils/download-file.util';
import { getFieldMappingDisplayLabel } from '../../../../utils/field-mapping-display-label.util';
import { resolveFieldOptions } from '../../../../utils/resolve-field-options.util';
import { getStepThreeFile } from '../../../../validations/add-template.validation';

type StepFourProps = Pick<StepContentProps, 'control' | 'getValues'>;

export const StepFour: FC<StepFourProps> = ({ control, getValues }) => {
  const { messagesLocale, messagesLocaleFn } = useLocale();
  const selectedEquityPlan = MappingEditorStore(({ selectedEquityPlan }) => selectedEquityPlan);
  const templateContext = MappingEditorStore(({ templateContext }) => templateContext);
  const variableMappingTypes = MappingEditorStore(
    ({ variableMappingTypes }) => variableMappingTypes,
  );
  const openPopup = TemplatePopupStore(({ openPopup }) => openPopup);
  const fieldOptions = useMemo(() => resolveFieldOptions(templateContext), [templateContext]);

  const stepOne = useWatch({ control, name: 'stepOne' });
  const stepTwo = useWatch({ control, name: 'stepTwo' });
  const stepThree = useWatch({ control, name: 'stepThree' });

  const signatories = stepTwo?.signatories ?? [];
  const signatoriesLabel = signatories
    .map((id) => messagesLocale[templateSignatoryTypeTranslation[id]] ?? id)
    .join(', ');

  const mappedFieldsCount = Object.keys(stepThree?.fieldMapping).filter(
    (k) => (stepThree?.fieldMapping[k]?.trim?.() ?? '') !== '',
  ).length;

  const fieldMappingsList = Object.entries(stepThree?.fieldMapping ?? {}).filter(
    ([, value]) => (value?.trim?.() ?? '') !== '',
  );

  const fieldMappingTypesForm = stepThree?.fieldMappingTypes ?? {};

  const signatoriesOrderLines = signatories.map(
    (id, i) => `${i + 1}. ${messagesLocale[templateSignatoryTypeTranslation[id]] ?? id}`,
  );

  const handleDownloadFile = () => {
    const file = getStepThreeFile(stepThree?.file);
    if (file) downloadFile(file);
  };

  return (
    <div className="flex h-full flex-col bg-gray-100">
      <div className="flex flex-1 flex-col gap-4 overflow-y-auto rounded-b-xl border-b border-gray-300 bg-white px-6 pb-4 pt-6">
        <section className="flex flex-col gap-2 rounded-lg bg-gray-50 pt-3">
          <span className="px-4 text-sm font-semibold text-gray-700">
            <AppFormattedMessage id="DOCUMENT" />
          </span>
          <div className="flex flex-col divide-y divide-gray-100 rounded-b-lg border border-gray-100 bg-white px-4">
            <SummaryItem
              className="py-3"
              title={<AppFormattedMessage id="DOCUMENT_NAME" />}
              titleClassName="w-fit"
              titleWrapperClassName="w-[120px]"
              value={stepOne?.name}
            />
            <SummaryItem
              className="py-3"
              title={<AppFormattedMessage id="CATEGORY" />}
              titleClassName="w-fit"
              titleWrapperClassName="w-[120px]"
              value={stepOne.category ? messagesLocale[categoryLabelKey[stepOne.category]] : '-'}
            />
            <SummaryItem
              className="py-3"
              title={<AppFormattedMessage id="SUB_CATEGORY" />}
              titleClassName="w-fit"
              titleWrapperClassName="w-[120px]"
              value={
                stepOne.subCategory ? messagesLocale[subCategoryLabelKey[stepOne.subCategory]] : '-'
              }
            />
            <SummaryItem
              className="py-3"
              title={<AppFormattedMessage id="EQUITY_PLAN" />}
              titleClassName="w-fit"
              titleWrapperClassName="w-[120px]"
              value={selectedEquityPlan?.name}
            />
            {stepOne.expirationPeriod && (
              <SummaryItem
                className="py-3"
                title={<AppFormattedMessage id="EXPIRATION_PERIOD" />}
                titleClassName="w-fit"
                titleWrapperClassName="w-[120px]"
                value={
                  <AppFormattedMessage id={expirationPeriodLabelKey[stepOne.expirationPeriod]} />
                }
              />
            )}
          </div>
        </section>

        <div className="flex flex-col divide-y divide-gray-100 rounded-lg border border-gray-100 bg-white px-4">
          <SummaryItem
            className="py-3"
            title={<AppFormattedMessage id="SIGNATORIES" />}
            titleClassName="w-fit"
            titleWrapperClassName="w-[120px]"
            value={signatoriesLabel}
          />
          <SummaryItem
            className="py-3"
            title={<AppFormattedMessage id="SIGNING_MODE" />}
            titleClassName="w-fit"
            titleWrapperClassName="w-[120px]"
            value={
              stepTwo?.signingMode ? messagesLocale[signingModeLabelKey[stepTwo?.signingMode]] : '-'
            }
          />
          {stepTwo?.signingMode === SigningMode.SET_ORDER && signatories.length > 0 && (
            <SummaryItem
              className="py-3"
              title={<AppFormattedMessage id="SIGNATORIES_ORDER" />}
              titleClassName="w-fit"
              titleWrapperClassName="w-[120px]"
              value={
                <ul className="list-none pl-0 text-left">
                  {signatoriesOrderLines.map((line) => (
                    <li className="text-xs text-gray-700" key={line}>
                      {line}
                    </li>
                  ))}
                </ul>
              }
            />
          )}
        </div>

        <div className="flex flex-col rounded-lg border border-gray-100 bg-white px-4">
          <SummaryItem
            className="pt-3"
            title={<AppFormattedMessage id="DOCUMENTS_LABEL" />}
            titleClassName="w-fit"
            titleWrapperClassName="w-[120px]"
            value={
              <button
                className="block w-full max-w-full cursor-pointer truncate text-left text-brand-700 underline"
                onClick={handleDownloadFile}
                title={getStepThreeFile(stepThree?.file)?.name ?? ''}
                type="button"
              >
                {getStepThreeFile(stepThree?.file)?.name ?? ''}
              </button>
            }
          />
          <SummaryItem
            className="pb-3"
            title={<AppFormattedMessage id="MAPPED_FIELDS" />}
            titleClassName="w-fit"
            titleWrapperClassName="w-[120px]"
            value={String(mappedFieldsCount)}
          />
        </div>

        <section className="flex flex-col gap-2 rounded-lg bg-gray-50 pt-3">
          <div className="flex items-center justify-between px-4">
            <span className="text-sm font-semibold text-gray-700">
              <AppFormattedMessage id="FIELD_MAPPINGS" />
            </span>
            <button
              aria-label="Edit field mappings"
              className="rounded p-1 transition-colors hover:bg-gray-200"
              onClick={() =>
                openPopup({
                  mapFieldsStepThreeSnapshot: cloneStepThreeSnapshot(getValues('stepThree')),
                  onAcceptCallback: () => {},
                  type: PopupType.MAP_FIELDS,
                })
              }
              type="button"
            >
              <EditIcon className="h-4 w-4" iconColor="#2565C8" />
            </button>
          </div>
          <div className="flex flex-col divide-y divide-gray-100 rounded-b-lg border border-gray-100 bg-white px-4">
            {fieldMappingsList.length > 0 ? (
              fieldMappingsList.map(([varName, storedValue]) => (
                <div className="flex gap-3 py-3" key={varName}>
                  <span className="w-[140px] flex-shrink-0 truncate text-xs font-medium text-gray-500">
                    {`{{${varName}}}`}
                  </span>
                  <span className="min-w-0 flex-1 text-xs font-medium text-gray-700 [overflow-wrap:anywhere]">
                    {getFieldMappingDisplayLabel({
                      fieldOptions,
                      mappingTypeExplicit: fieldMappingTypesForm[varName],
                      messagesLocale,
                      messagesLocaleFn,
                      storedValue,
                      variableMappingTypeFromStore: variableMappingTypes[varName],
                    })}
                  </span>
                </div>
              ))
            ) : (
              <div className="px-4 py-3 text-xs text-gray-500">—</div>
            )}
          </div>
        </section>
      </div>
    </div>
  );
};
